import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DashboardShell,
  NotificationStrip,
  createShellNavLink,
  type ShellNavItem,
} from '@vipso/shared';
import {
  IconHome,
  IconChecklist,
  IconPackage,
  IconShoppingCart,
  IconUsers,
  IconBuildingStore,
  IconSettings,
  IconUserCheck,
  IconBuildingWarehouse,
  IconTruck,
  IconHammer,
  IconApps,
  IconMapPin,
  IconArrowsTransferDown,
  IconShieldHalfFilled,
  IconHierarchy2,
} from '@tabler/icons-react';
import { logout } from '../store/authSlice';
import type { RootState } from '../store';
import { resolveDisplayLogoUrl } from '../utils/resolveDisplayLogoUrl';
import { useSystemNotificationsQuery } from '../api/enhanceGenerated';

const ShellNavLink = createShellNavLink(NavLink);

export function AppShellLayout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, org, theme, config, pendingTaskCount } = useSelector(
    (state: RootState) => state.auth,
  );

  const navItems: ShellNavItem[] = [
    { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    { to: '/customers', label: t('nav.customers'), icon: IconUsers },
    { to: '/vendors', label: t('nav.vendors'), icon: IconBuildingStore },
    { to: '/products', label: t('nav.products'), icon: IconPackage },
    { to: '/orders', label: t('nav.orders'), icon: IconShoppingCart },
    { to: '/staff', label: t('nav.staff'), icon: IconUserCheck },
    { to: '/warehouse', label: t('nav.warehouse'), icon: IconBuildingWarehouse },
    { to: '/storage', label: t('nav.storage'), icon: IconMapPin },
    { to: '/inventory', label: t('inventory.title'), icon: IconArrowsTransferDown },
    { to: '/transport', label: t('nav.transport'), icon: IconTruck },
    { to: '/labour', label: t('nav.labour'), icon: IconHammer },
    { to: '/roles', label: t('roles.title'), icon: IconShieldHalfFilled },
    { to: '/approval-flows', label: t('nav.approvalFlows'), icon: IconHierarchy2 },
    { to: '/tasks', label: t('nav.tasks'), icon: IconChecklist },
    { to: '/settings', label: t('nav.settings'), icon: IconSettings },
  ];

  const mobileNavItems: ShellNavItem[] = [
    { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    { to: '/tasks', label: t('nav.tasks'), icon: IconChecklist },
    { to: '/products', label: t('nav.products'), icon: IconPackage },
    { to: '/orders', label: t('nav.orders'), icon: IconShoppingCart },
    {
      to: '/more',
      label: t('nav.more'),
      icon: IconApps,
      activePatterns: [
        '/customers',
        '/vendors',
        '/staff',
        '/warehouse',
        '/storage',
        '/inventory',
        '/transport',
        '/labour',
        '/roles',
        '/approval-flows',
        '/settings',
      ],
    },
  ];

  const mobileDrawerItems: ShellNavItem[] = [
    { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    { to: '/tasks', label: t('nav.tasks'), icon: IconChecklist },
    { to: '/products', label: t('nav.products'), icon: IconPackage },
    { to: '/orders', label: t('nav.orders'), icon: IconShoppingCart },
    { to: '/more', label: t('nav.more'), icon: IconApps },
    { to: '/settings', label: t('nav.settings'), icon: IconSettings },
  ];

  const { data: notifData } = useSystemNotificationsQuery(undefined, {
    pollingInterval: 300_000,
  });
  const notifications = notifData?.systemNotifications ?? [];

  const tasksLabel =
    pendingTaskCount > 0
      ? t('nav.tasksWithCount', { count: pendingTaskCount })
      : t('nav.tasks');
  const displayLogoUrl = resolveDisplayLogoUrl(org, config, theme);

  const desktopNav = navItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );
  const mobileNav = mobileNavItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );
  const drawerNav = mobileDrawerItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );

  function handleLogout() {
    dispatch(logout());
    navigate('/boot', { replace: true });
  }

  return (
    <DashboardShell
      LinkComponent={ShellNavLink}
      navItems={desktopNav}
      mobileNavItems={mobileNav}
      mobileDrawerItems={drawerNav}
      currentPath={location.pathname}
      brandTitle="VIPSo"
      logoUrl={displayLogoUrl}
      orgName={org?.name}
      user={user}
      notificationBanner={
        notifications.length > 0 ? (
          <NotificationStrip notifications={notifications} />
        ) : undefined
      }
      profileLinks={[
        {
          label: t('nav.settings'),
          onClick: () => navigate('/settings'),
        },
        {
          label: t('nav.customers'),
          onClick: () => navigate('/customers'),
        },
        {
          label: t('nav.vendors'),
          onClick: () => navigate('/vendors'),
        },
        {
          label: t('orgPicker.switchOrganization'),
          onClick: () => navigate('/org-picker'),
        },
      ]}
      onLogout={handleLogout}
    >
      <Outlet />
    </DashboardShell>
  );
}
